import { useCallback, useEffect, useRef } from "react";

/**
 * Custom hook to manage notification dismissal logic
 * Handles click-to-dismiss functionality for game log notifications
 */
export const useNotificationDismissal = (logNotifications, removeNotification) => {
  const notificationRefs = useRef({});
  const dismissingRef = useRef(new Set()); // Track which notifications are currently dismissing

  // Handle click anywhere on page to dismiss oldest notification
  const handlePageClick = useCallback((e) => {
    // Check if click is within the notifications container
    const notificationsContainer = e.target.closest('.game-log-notifications-container');
    if (notificationsContainer) {
      return; // Don't dismiss if clicking on notifications
    }
    
    // Find the oldest notification that isn't already dismissing
    // Notifications array has oldest first (index 0 = oldest, index length-1 = newest)
    // Notifications are displayed reversed (newest first at top, oldest last at bottom)
    // We want to dismiss from the bottom (oldest first)
    if (logNotifications.length === 0) return;
    
    // Start from the beginning (index 0, oldest notification)
    // Since the array has oldest first, we dismiss from the start of the array
    for (let i = 0; i < logNotifications.length; i++) {
      const notification = logNotifications[i];
      const ref = notificationRefs.current[notification.id];
      
      // Check if notification exists and has a dismiss method
      if (ref && ref.dismiss) {
        // Check if it's already dismissing
        const isAlreadyDismissing = ref.isDismissing ? ref.isDismissing() : dismissingRef.current.has(notification.id);
        
        if (!isAlreadyDismissing) {
          // Mark as dismissing and dismiss it
          dismissingRef.current.add(notification.id);
          ref.dismiss();
          // Remove from dismissing set after fade out completes
          setTimeout(() => {
            dismissingRef.current.delete(notification.id);
          }, 350); // Slightly longer than animation to ensure cleanup
          break; // Only dismiss one at a time
        }
      }
    }
  }, [logNotifications]);

  // Add click listener to game container
  useEffect(() => {
    const gameContainer = document.querySelector('.game');
    if (gameContainer) {
      gameContainer.addEventListener('click', handlePageClick);
      return () => {
        gameContainer.removeEventListener('click', handlePageClick);
      };
    }
  }, [handlePageClick]);

  return {
    notificationRefs,
    dismissingRef
  };
};

